import { giphyLocalUrl } from '@/app/util/env'
import { Interface, NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { getCookie, getPost } from '@/util/url'
import { CSRF_COOKIE_NAME } from '@/util/user'
import { useState } from 'react'
import { useFormState } from 'react-dom'
import Button from '../button'

const REASONS_DESCRIPTION = {
    animal_welfare: 'Animal welfare',
    bullying_harassment: 'Bullying and harassment',
    bullying_harassment_women: 'Bullying and harassment of women',
    clips_copyright: 'Clips Copyright',
    coordinated_harm: 'Coordinated harm or threat to life',
    copyright: 'Copyright infringement',
    csam: 'CSAM',
    csam_deepfake: 'CSAM deepfake',
    csam_jokes: 'CSAM jokes or mockery',
    disasters_tragedies: 'Disasters and tragedies',
    discrimination: 'Discrimination',
    eating_disorder: 'Eating disorder',
    epilepsy: 'Epilepsy',
    graphic_medical_content: 'Graphic medical content',
    hate: 'Hate',
    illegal_activity: 'Illegal content: Illegal activity',
    illegal_substances: 'Illegal content: Selling illegal substances/service',
    incitement_violence_women: 'Incitement to violence against women',
    incitement_violence_hate: 'Incitement to violence or hate',
    private_information: 'Private information',
    misinformation: 'Misinformation',
    mutilation: 'Mutilation',
    nonconsensual_deepfake: 'Non-consensual deepfake',
    nonconsensual_image: 'Non-consensual image',
    nudity: 'Nudity',
    perpetrators: 'Perpetrators of violence',
    pornography: 'Pornography',
    public_health: 'Public health',
    real_gore: 'Real gore',
    school_shootings: 'School shootings',
    sexual_violence: 'Sexual violence',
    solicitation: 'Solicitation',
    spam: 'Spam',
    self_harm: 'Suicide',
    terrorism: 'Terrorism and violent extremists',
    threatening_stalking: 'Threatening or stalking',
    threatening_stalking_women: 'Threatening or stalking of women',
    trademark: 'Trademark violation',
    underage_user: 'Underage user',
    violence: 'Violence',
} as const

type Reason = keyof typeof REASONS_DESCRIPTION
const REASONS_ORDER: Reason[] = [
    'animal_welfare',
    'bullying_harassment',
    'bullying_harassment_women',
    'clips_copyright',
    'coordinated_harm',
    'copyright',
    'csam',
    'csam_deepfake',
    'csam_jokes',
    'disasters_tragedies',
    'discrimination',
    'eating_disorder',
    'epilepsy',
    'graphic_medical_content',
    'hate',
    'illegal_activity',
    'illegal_substances',
    'incitement_violence_women',
    'incitement_violence_hate',
    'private_information',
    'misinformation',
    'mutilation',
    'nonconsensual_deepfake',
    'nonconsensual_image',
    'nudity',
    'perpetrators',
    'pornography',
    'public_health',
    'real_gore',
    'school_shootings',
    'sexual_violence',
    'solicitation',
    'spam',
    'self_harm',
    'terrorism',
    'threatening_stalking',
    'threatening_stalking_women',
    'trademark',
    'underage_user',
    'violence',
]

type Props = {
    onClose: () => void
    gifIds: (string | number)[]
}

export const contentTakedown = (
    gifIds: (string | number)[],
    reason: string,
    additionalInfo: string,
    shouldEmailUser: boolean
) => {
    const params = {
        takedowns: gifIds.map((gifId) => ({
            reason: reason,
            gif_id: gifId,
        })),
        send_email: shouldEmailUser,
        note: additionalInfo || undefined,
    }
    return fetch(`${giphyLocalUrl}/api/v3/content-takedown/takedown`, {
        ...getPost(getCookie(CSRF_COOKIE_NAME), false),
        body: JSON.stringify(params),
    })
}

const reasonInput = 'reason'
const noteInput = 'note'
const sendEmailName = 'sendemail'

const ContentTakedown = ({ onClose, gifIds }: Props) => {
    const [isPending, setPending] = useState(false)
    const submitClicked = async (_: string, queryData: FormData) => {
        setPending(true)
        try {
            await contentTakedown(
                gifIds,
                queryData.get(reasonInput) as string,
                queryData.get(noteInput) as string,
                queryData.get(sendEmailName) === 'on'
            )
        } catch (error) {
            if (error instanceof Error) {
                console.error(error.message)
                return error.message
            } else {
                throw error
            }
        } finally {
            setPending(false)
        }
        return 'Success'
    }
    const [state, formAction] = useFormState(submitClicked, '')

    return state === 'Success' ? (
        <div className={`flex justify-center p-20 text-center text-lg ${NexaBlack.variable}`}>Gif deleted.</div>
    ) : (
        <form
            className={cn(
                `group relative m-4 flex flex-col gap-2 ${Interface.variable}`,
                isPending && 'pointer-events-none opacity-80'
            )}
            action={formAction}
        >
            <h1 className="text-lg font-bold">Why are you removing the selected content?</h1>
            <div className="mb-4 grid grid-cols-4 gap-4">
                {REASONS_ORDER.slice().map((key) => (
                    <div className="flex items-start gap-2" key={key}>
                        <input type="radio" id={key} value={key} name={reasonInput} required />
                        <label className="select-none" htmlFor={key}>
                            {REASONS_DESCRIPTION[key]}
                        </label>
                    </div>
                ))}
            </div>
            <h1 className="text-lg font-bold">Any additional information?</h1>
            <textarea className="my-1 h-20 w-full resize-none" name={noteInput} />
            <div className="my-4 flex gap-2">
                <input type="checkbox" name={sendEmailName} id={sendEmailName} />{' '}
                <label htmlFor={sendEmailName}>Send email to user</label>
            </div>
            <div className="flex flex-col justify-center gap-2">
                <Button
                    type="submit"
                    className="cursor-pointer bg-gradient-primary group-invalid:pointer-events-none group-invalid:opacity-60"
                >
                    Submit
                </Button>
                <Button onClick={onClose} className="bg-giphyDarkCharcoal">
                    Cancel
                </Button>
            </div>
        </form>
    )
}

export default ContentTakedown
